import { ChevronLeft, Clock, Info, Pencil, TriangleAlert, X as XIcon } from 'lucide-react'
import type { OutboxEntry } from '../../lib/mail-outbox'
import { cn } from '../../lib/utils'
import { formatClock } from './offline-bar'

// Outbox page inside the mail list pane (#785, mockup 1d „Ausgang"): no route of
// its own, the list pane swaps to it. Error badges use fixed German texts per
// code, never a server message.

const ERROR_TEXTS: Record<string, string> = {
  MAIL_ACCOUNT_MISSING: 'Konto gibt es nicht mehr',
  MAIL_ACCOUNT_DISABLED: 'Konto ist deaktiviert',
  MAIL_PROTECTED_CONTEXT: 'Geschützter Inhalt – nicht gesendet',
  MAIL_IDEMPOTENCY_MISMATCH: 'Anderes Konto – bitte neu senden',
  MAIL_IDEMPOTENCY_INVALID: 'Nicht gesendet – bitte neu senden',
  HTTP_400: 'Nicht gesendet – Angaben prüfen',
  MAIL_SEND_IN_PROGRESS: 'Senden hängt auf dem Server – bitte prüfen',
  HTTP_404: 'Konto oder Entwurf nicht mehr vorhanden',
  HTTP_413: 'Zu groß zum Senden',
}

/** Fixed badge text for a failed entry. */
export function outboxErrorText(code: string | undefined): string {
  return (code && ERROR_TEXTS[code]) || 'Nicht gesendet – bitte prüfen'
}

/** First recipient as shown on the card: display name, else the address. */
export function recipientLabel(to: string): string {
  const first = to.split(',')[0]?.trim() ?? ''
  // Trim before the quotes go: '"Name" <addr>' leaves a space after the closing quote.
  const name = first
    .replace(/<[^>]*>/, '')
    .trim()
    .replace(/^"|"$/g, '')
    .trim()
  if (name) return name
  return (/<([^>]+)>/.exec(first)?.[1] ?? first).trim() || '(Kein Empfänger)'
}

function initials(label: string): string {
  const base = label.includes('@') ? label.split('@')[0] : label
  return (
    base
      .split(/[\s._-]+/)
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .slice(0, 2)
      .join('') || '?'
  )
}

export function OutboxPanel({
  entries,
  busyId,
  onBack,
  onEdit,
  onCancel,
  canEdit,
}: {
  entries: readonly OutboxEntry[]
  /** Entry whose „Abbrechen" is running (buttons disabled). */
  busyId: string | null
  /** „Bearbeiten" only for an account known unprotected (#524); else only
   * „Abbrechen" (→ draft in the same account). */
  canEdit: (entry: OutboxEntry) => boolean
  onBack: () => void
  onEdit: (entry: OutboxEntry) => void
  onCancel: (entry: OutboxEntry) => void
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-4 phone:px-4">
      <button
        type="button"
        onClick={onBack}
        className="-ml-2 flex min-h-11 w-fit items-center gap-1 px-2 text-[13px] font-medium text-text-primary"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        Posteingang
      </button>
      <h2 className="text-[22px] font-semibold leading-8">Ausgang</h2>
      <p className="mb-3 text-xs text-text-tertiary">Mails, die im Funkloch geschrieben wurden</p>
      <ul className="space-y-2">
        {entries.map((entry) => {
          const to = recipientLabel(entry.mail.to)
          const preview = (entry.mail.text ?? '').replace(/\s+/g, ' ').trim().slice(0, 160)
          const busy = busyId === entry.id
          const editable = canEdit(entry)
          return (
            <li
              key={entry.id}
              className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated"
            >
              <div className="space-y-2 p-3">
                <div className="flex items-start gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent">
                    {initials(to)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-text-primary">
                      {entry.mail.subject || '(Kein Betreff)'}
                    </p>
                    <p className="truncate text-[11px] text-text-tertiary">
                      An {to} · geschrieben {formatClock(entry.createdAt)}
                    </p>
                  </div>
                </div>
                {preview && (
                  <p className="line-clamp-2 text-[13px] leading-relaxed text-text-secondary">
                    {preview}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {entry.status === 'fehler' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-danger/15 px-2 py-0.5 text-2xs font-medium text-danger">
                      <TriangleAlert className="h-3 w-3" aria-hidden="true" />
                      {outboxErrorText(entry.fehlerCode)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-info/15 px-2 py-0.5 text-2xs font-medium text-info">
                      <Clock className="h-3 w-3" aria-hidden="true" />
                      {entry.status === 'entwurf'
                        ? 'wird Entwurf, sobald online'
                        : 'wird gesendet, sobald online'}
                    </span>
                  )}
                  <span className="text-2xs text-text-tertiary">nur auf diesem Gerät</span>
                </div>
              </div>
              <div className="flex gap-2 border-t border-border-subtle p-2">
                <button
                  type="button"
                  disabled={busy || !editable}
                  title={editable ? undefined : 'Geschütztes Konto – nur Abbrechen möglich'}
                  onClick={() => onEdit(entry)}
                  className="flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-lg border border-border-default text-[13px] font-medium text-text-primary disabled:opacity-50"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  Bearbeiten
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onCancel(entry)}
                  className={cn(
                    'flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-lg text-[13px] font-medium text-danger disabled:opacity-50',
                  )}
                >
                  <XIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Abbrechen
                </button>
              </div>
            </li>
          )
        })}
      </ul>
      <p className="mt-3 flex gap-2 px-1 text-[11px] leading-relaxed text-text-tertiary">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          „Abbrechen“ macht daraus einen Entwurf – nichts geht verloren, nichts wird gesendet. Eine
          Mail geht genau einmal raus, auch wenn die Verbindung wackelt.
        </span>
      </p>
    </div>
  )
}
